<template>
	<ion-button
		v-if="!user.admin"
		size="small"
		color="warning"
		@click="makeAdmin"
	>
		{{ $t("user-modal.make-admin") }}
	</ion-button>
	<ion-button v-else size="small" color="danger" @click="removeAdmin">
		{{ $t("user-modal.remove-admin") }}
	</ion-button>
</template>

<script setup lang="ts">
	import { alertController, IonButton } from "@ionic/vue";
	import { useSetUserAdmin } from "seedgreen-shared/queries/user.js";

	import { useI18n } from "vue-i18n";
	import { GVAUserPrivilegedDTO } from "seedgreen-shared/dtos/GVAUserDTO";
	import { showLoader } from "@/utils/loader";

	const props = defineProps<{
		user: GVAUserPrivilegedDTO;
	}>();

	const { t } = useI18n();
	const loadingMessage = t("common.loading-dot");

	const { setUserAdmin } = useSetUserAdmin();

	async function makeAdmin() {
		const alert = await alertController.create({
			header: t("user-management.make-admin-header"),
			message: t("user-management.make-admin-message", {
				name: props.user.name,
			}),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("user-modal.make-admin"),
					handler: async () => await setAdmin(true),
				},
			],
		});
		await alert.present();
	}

	async function removeAdmin() {
		const alert = await alertController.create({
			header: t("user-management.remove-admin-header"),
			message: t("user-management.remove-admin-message", {
				name: props.user.name,
			}),
			buttons: [
				{
					text: t("common.cancel"),
					role: "cancel",
				},
				{
					text: t("user-modal.remove-admin"),
					role: "destructive",
					handler: async () => await setAdmin(false),
				},
			],
		});
		await alert.present();
	}

	async function setAdmin(admin: boolean) {
		const dismiss = await showLoader(loadingMessage);
		try {
			await setUserAdmin({
				userId: props.user.id,
				admin,
			});
		} finally {
			await dismiss();
		}
	}
</script>
